import { timeParse } from 'd3-time-format';
import { getCustomXDomain, yTickFormatter } from './chartUtils';

// End-value labels used in multiple charts
const endValLabels = [
  { president: 'clinton-1993', label: 'Clinton' },
  { president: 'w-bush-2001', label: 'Bush' },
  { president: 'obama-2009', label: 'Obama' },
  { president: 'trump-2017', label: 'Trump 1.0' },
  { president: 'biden-2021', label: 'Biden' },
  { president: 'trump-2025', label: 'Trump 2.0' },
];

// Custom sort order to order the lines
const categoriesOrderLines = [
  'clinton-1993',
  'w-bush-2001',
  'obama-2009',
  'biden-2021',
  'trump-2017',
  'trump-2025',
];

// set colours
// const darkRed = '#C72C41';
const lightGrey = '#ccc';
const midGrey = '#666';
const orange = '#d95f02';
// const lightOrange = '#fc8d62';
const turquoise = '#00a5cb';

const global = [
  '#333333',
  '#66C5CC',
  '#F6CF71',
  '#F89C74',
  '#DCB0F2',
  '#87C55F',
  '#9EB9F3',
  '#FE88B1',
  '#C9DB74',
  '#8BE0A4',
  '#B497E7',
  '#D3B484',
  '#B3B3B3',
];

const dateParserFn = timeParse('%d/%m/%y');

const tariff = dateParserFn('02/04/25');

///////////////////
/** Chart props */
///////////////////

/** S&P crown chart */
export const spMultipleLineChartProps = {
  rollingAvg: 3, // Comment this out to disable rolling average
  xValKey: 'date',
  yValKey: 's&p',
  categoryKey: 'president',
  height: 600,
  //#151515,#3b3b3b,#666666,#949494,#c5c5c5
  // Adjut colours
  lineColours: [
    { president: 'clinton-1993', colour: lightGrey },
    { president: 'w-bush-2001', colour: lightGrey },
    { president: 'obama-2009', colour: lightGrey },
    { president: 'biden-2021', colour: lightGrey },
    { president: 'trump-2017', colour: midGrey },
    { president: 'trump-2025', colour: orange },
  ],
  highlightCategory: 'trump-2025',
  // How the date values in the data are formatted. E.g. "%d/%m/%y"
  dateParser: '%d/%m/%y',
  // The date format for the x-axis
  dateFormatter: '%b. %-d',
  // Custom y-axis domain
  customYDomain: [-20, 10] as [number, number],
  // Whether to start the y-axis from 0
  zeroBased: false,
  // Custom x ticks at Jan. 20 and April 30
  customXTicks: [dateParserFn('20/01/25'), dateParserFn('30/04/25')],
  // Number of y ticks
  yTicks: 5,
  // Custom end-value chart labels
  endValLabels,
  //how much room in px do you need on the right side to show labels
  endValMargin: 75,
  //on chart text plus positions
  annotations: [
    {
      text: 'Percent change in the S&P 500 over each president’s first 100 days',
      cls: 'annotation-pct-change',
      xPos: dateParserFn('20/01/25'),
      yPos: -6,
      width: 285,
    },
  ],
  yTickFormatter,
  // Custom sort order to order the lines
  categoriesOrder: categoriesOrderLines,
  tariffDate: tariff,
};

/** VIX market volatility chart */
export const vixMultipleLineChartProps = {
  categoriesOrder: categoriesOrderLines,
  xValKey: 'date',
  yValKey: 'volatility',
  categoryKey: 'president',
  highlightCategory: 'trump-2025',
  lineColours: [
    { president: 'clinton-1993', colour: lightGrey },
    { president: 'w-bush-2001', colour: lightGrey },
    { president: 'obama-2009', colour: lightGrey },
    { president: 'biden-2021', colour: lightGrey },
    { president: 'trump-2017', colour: midGrey },
    { president: 'trump-2025', colour: orange },
  ],
  endValLabels,
  customXTicks: [dateParserFn('20/01/25'), dateParserFn('30/04/25')],
  xTicks: 6,
  //how much room in px do you need on the right side to show labels
  endValMargin: 80,
  dateParser: '%d/%m/%y',
  dateFormatter: '%b. %-d',
  unit: '',
  customYDomain: [10, 55] as [number, number],
  zeroBased: false,
};

/** Global markets by president small multiples */
export const globalMktSmallMultiplesProps = {
  rollingAvg: 3, // Comment this out to disable rolling average
  height: 270,
  xValKey: 'date',
  yValKey: 'index-change',
  categoryKey: 'president',
  childCategoryKey: 'region', // categories within each small multiple
  dateParser: '%d/%m/%Y',
  dateFormatter: '%b. %-d',
  zeroBased: false,
  endDotDiameter: 8,
  customYDomain: [-20, 30] as [number, number],

  // Custom sort order for the small multiples
  categoriesOrder: [
    'clinton-1993',
    'w-bush-2001',
    'obama-2009',
    'trump-2017',
    'biden-2021',
    'trump-2025',
  ],
  // Custom mini chart titles
  chartTitles: [
    { president: 'clinton-1993', title: 'Clinton' },
    { president: 'w-bush-2001', title: 'Bush' },
    { president: 'obama-2009', title: 'Obama' },
    { president: 'trump-2017', title: 'Trump 1.0' },
    { president: 'biden-2021', title: 'Biden' },
    { president: 'trump-2025', title: 'Trump 2.0' },
  ],
  // Custom colour setting:
  lineColours: [
    { region: 'us', colour: global[0] },
    { region: 'asia-ex-japan', colour: global[1] },
    { region: 'china', colour: global[2] },
    { region: 'emerging-market', colour: global[3] },
    { region: 'europe', colour: global[4] },
    { region: 'japan', colour: global[5] },
  ],
  // Custom end values
  endValLabels: [
    { region: 'us', label: 'U.S.' },
    { region: 'asia-ex-japan', label: '' },
    { region: 'china', label: '' },
    { region: 'emerging-market', label: '' },
    { region: 'europe', label: '' },
    { region: 'japan', label: '' },
    // { region: 'asia-ex-japan', label: 'Asia ex Japan' },
    // { region: 'china', label: 'China' },
    // { region: 'emerging-market', label: 'Emerging market' },
    // { region: 'europe', label: 'Europe' },
    // { region: 'japan', label: 'Japan' },
  ],
  // Custom end values
  legendLabels: [
    { region: 'us', label: 'U.S. (S&P 500)' },
    { region: 'asia-ex-japan', label: 'Asia except Japan (MSCI)' },
    { region: 'china', label: 'China (CSI 300)' },
    { region: 'emerging-market', label: 'Emerging markets (MSCI)' },
    { region: 'europe', label: 'Europe (STOXX 600)' },
    { region: 'japan', label: 'Japan (Nikkei 225)' },
  ],
  getCustomXDomain,
  //how much room in px do you need on the right side to show labels
  endValMargin: 40,
  yTickFormatter,
};

/** Gold prices chart */
export const goldMultipleLineChartProps = {
  categoriesOrder: categoriesOrderLines,
  xValKey: 'date',
  yValKey: 'gold',
  categoryKey: 'president',
  highlightCategory: 'trump-2025',
  lineColours: [
    { president: 'clinton-1993', colour: lightGrey },
    { president: 'w-bush-2001', colour: lightGrey },
    { president: 'obama-2009', colour: lightGrey },
    { president: 'trump-2017', colour: midGrey },
    { president: 'biden-2021', colour: lightGrey },
    { president: 'trump-2025', colour: orange },
  ],
  // Custom end-value chart labels
  endValLabels,
  // How the date values in the data are formatted. E.g. "%d/%m/%y"
  dateParser: '%d/%m/%y',
  // The date format for the x-axis
  dateFormatter: '%b. %-d',
  // Custom y-axis domain
  customYDomain: [-10, 27] as [number, number],
  // Custom x ticks at Jan. 20 and April 30
  customXTicks: [dateParserFn('20/01/25'), dateParserFn('30/04/25')],
  // Whether to start the y-axis from 0
  zeroBased: false,
  // Number of x ticks
  xTicks: 2,
  // Number of y ticks
  yTicks: 4,
  //how much room in px do you need on the right side to show labels
  endValMargin: 70,
  yTickFormatter,
  id: 'gold-chart'
};

/** Bonds chart */
export const bondMultipleLineChartProps = {
  categoriesOrder: ['UK', 'Germany', 'Japan', 'U.S.'],
  xValKey: 'date',
  yValKey: 'yield',
  categoryKey: 'country',
  highlightCategory: 'U.S.',
  lineColours: [
    { country: 'UK', colour: global[1] },
    { country: 'Germany', colour: global[4] },
    { country: 'Japan', colour: global[5] },
    { country: 'U.S.', colour: global[0] },
  ],
  // Custom end-value chart labels
  endValLabels: [
    { country: 'UK', label: 'UK' },
    { country: 'Germany', label: 'Germany' },
    { country: 'Japan', label: 'Japan' },
    { country: 'U.S.', label: 'U.S.' },
  ],
  // How the date values in the data are formatted. E.g. "%d/%m/%y"
  dateParser: '%d/%m/%y',
  // The date format for the x-axis
  dateFormatter: '%b. %-d',
  // Custom y-axis domain
  unit: ' basis points',
  customYDomain: [-60, 50] as [number, number],
  customXDomain: [dateParserFn('20/01/25'), dateParserFn('30/04/25')],
  // Custom x ticks at Jan. 20 and April 30
  customXTicks: [dateParserFn('20/01/25'), dateParserFn('30/04/25')],
  // Whether to start the y-axis from 0
  zeroBased: false,
  // Number of x ticks
  xTicks: 2,
  // Number of y ticks
  yTicks: 5,
  //how much room in px do you need on the right side to show labels
  endValMargin: 73,
  yTickFormatter,
};

/** USD prices chart */
export const currencyMultipleLineChartProps = {
  rollingAvg: 3,
  categoriesOrder: categoriesOrderLines,
  xValKey: 'date',
  yValKey: 'currency',
  categoryKey: 'president',
  highlightCategory: 'trump-2025',
  lineColours: [
    { president: 'clinton-1993', colour: lightGrey },
    { president: 'w-bush-2001', colour: lightGrey },
    { president: 'obama-2009', colour: lightGrey },
    { president: 'trump-2017', colour: midGrey },
    { president: 'biden-2021', colour: lightGrey },
    { president: 'trump-2025', colour: orange },
  ],
  // Custom end-value chart labels
  endValLabels,
  // How the date values in the data are formatted. E.g. "%d/%m/%y"
  dateParser: '%d/%m/%y',
  // The date format for the x-axis
  dateFormatter: '%b. %-d',
  // Custom y-axis domain
  customYDomain: [-10, 6] as [number, number],
  // Custom x ticks at Jan. 20 and April 30
  customXTicks: [dateParserFn('20/01/25'), dateParserFn('30/04/25')],
  // Whether to start the y-axis from 0
  zeroBased: false,
  // Number of x ticks
  xTicks: 2,
  // Number of y ticks
  yTicks: 6,
  //how much room in px do you need on the right side to show labels
  endValMargin: 85,
  yTickFormatter,
  id: 'dollar-chart'
};

/** Fed US GDP growth forecast bar chart */
export const usGdpGrowthBarChartProps = {
  height: 450,
  xValKey: 'date',
  yValKey: 'new-projection',
  stackedYValKey: 'initial-projection',

  barColour: lightGrey,
  stackedBarColour: turquoise, // Controls the colour of the stripes

  customXTicks: ['1995', '2000', '2005', '2010', '2015', '2020', '2025'],
  customYDomain: [-4, 6.7] as [number, number],
  zeroBased: false,
  unit: '%',
  yTickFormatter,
  barWidthRatio: 1.15, // Controls fatness of the bars
  //how much room in px do you need on the right side to show labels
  endValMargin: 112,

  //on chart text plus positions
  annotations: [
    {
      text: 'FORECAST',
      cls: 'annotation-forecast',
      xPos: '2025',
      xPosMargin: 5,
      yPos: 5.5,
      width: 50,
    },
    {
      text: 'Previous forecast',
      cls: 'annotation-original-forecast bold',
      withLeaderLine: true,
      xPos: '2027',
      xPosMargin: 10,
      yPos: 2.35,
      width: 90,
    },
    {
      text: 'Latest forecast',
      cls: 'annotation-latest-forecast bold',
      withLeaderLine: true,
      xPos: '2027',
      xPosMargin: 10,
      yPos: 1,
      width: 90,
    },
  ],
};
